/* eslint-disable max-classes-per-file */
import moment from "moment";

export interface IPeriodSelected {
    type: string; // period, month, week, date
    from: Date;
    to: Date;
    periodMonth: Date;
}

export interface IAggregatPeriod {
    key: string;
    format: string;
    name: string;
    periodStartDate: Date;
    periodEndDate: Date;
    isCurrentlyActive: boolean;
    isWithin(from: Date, to: Date): boolean;
    getStartOrLater(laterCandidate: Date): Date;
    getEndOrEarlier(soonerCandidate: Date): Date;
}

export class AggregatPeriod implements IAggregatPeriod {
    key: string;
    format: string;
    name: string;
    periodStartDate: Date;
    periodEndDate: Date;
    isCurrentlyActive: boolean;

    constructor(adapter: any, m: moment.Moment) {
        const today = moment();
        this.key = m.format(adapter.format);
        this.format = adapter.format;
        this.name = adapter.name(m);
        this.periodStartDate = adapter.start(m).toDate();
        this.periodEndDate = adapter.end(m).toDate();
        this.isCurrentlyActive = adapter.end(m).isAfter(today);
    }

    isWithin(from: Date, to: Date) {
        return moment(from).isSameOrBefore(this.periodEndDate) &&
            moment(to).isSameOrAfter(this.periodStartDate);
    }

    getStartOrLater(laterCandidate: Date): Date {
        return (moment(this.periodStartDate).isSameOrAfter(laterCandidate)) ? this.periodStartDate : laterCandidate;
    }

    getEndOrEarlier(soonerCandidate: Date): Date {
        return (moment(this.periodEndDate).isSameOrBefore(soonerCandidate)) ? this.periodEndDate : soonerCandidate;
    }
}

export class AggregatPeriods {
    static create(languageCode: string, periodSelected: IPeriodSelected): IAggregatPeriod[] {
        const capitilizeString = (str: string): string => {
            const trimmed = str.replace(/\u200E/, ""); // Fix för UTF-16 space i IE
            return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
        };

        const dateRange = (start: moment.Moment, end: moment.Moment, unitOfTime: moment.unitOfTime.DurationConstructor) => {
            const range: moment.Moment[] = [];
            for (let date = start.startOf("day"); date <= end.endOf("day"); date = date.add(1, unitOfTime)) {
                range.push(date.clone());
            }
            return range;
        };

        const isPartialWeek = (date: moment.Moment, unitOfTime: moment.unitOfTime.DurationConstructor): boolean => {
            const start = date.clone().startOf(unitOfTime);
            const end = date.clone().endOf(unitOfTime);

            const samePeriod = start.month() === end.month() && start.isoWeek() === end.isoWeek();
            return !samePeriod;
        };

        const periodAdapters = {

            PERIOD: {
                unitOfTime: "month",
                format: "YY-M",
                start: fromDate => moment(fromDate).startOf("month").locale(languageCode),
                name: date => {
                    const monthName = capitilizeString(date.locale(languageCode).format("MMMM"));
                    return monthName.length > 4 ? `${monthName.substring(0, 3)}` : monthName;
                },
                end: fromDate => moment(fromDate).endOf("month")
            },

            MANAD: {
                unitOfTime: "week",
                format: "GG-W",
                start: fromDate => moment(fromDate).startOf("week").locale(languageCode),
                name: date => `${date.format("[v]w")}${isPartialWeek(date, "week") ? "*" : ""}`,
                end: fromDate => moment(fromDate).endOf("week")
            },

            VECKA: {
                unitOfTime: "day",
                format: "YY-M-D",
                start: fromDate => moment(fromDate).startOf("day").locale(languageCode),
                name: date => capitilizeString(date.format("dd D/MM")),
                end: fromDate => moment(fromDate).endOf("day")
            }
        };

        const adapter = periodAdapters[periodSelected.type];
        return dateRange(adapter.start(periodSelected.from), moment(periodSelected.to), adapter.unitOfTime)
            .map(momnt => new AggregatPeriod(adapter, momnt));
    }
}
